import React from "react";
import { Box, Container, Text, SimpleGrid, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { AlertTriangle, HelpCircle } from "lucide-react";

const MotionBox = motion(Box);

const painPoints = [
  "Scattered PDFs",
  "Long, unfocused videos",
  "Notes passed down from seniors",
  "Disconnected practice questions",
];

const doubts = [
  "What really matters for exams",
  "What actually matters in practice",
];

const SectionLabel = ({ children }) => (
  <Text
    fontFamily="'Inter', sans-serif"
    fontSize="0.8125rem"
    fontWeight="600"
    letterSpacing="0.1em"
    textTransform="uppercase"
    color="#0095C8"
    mb={2}
  >
    {children}
  </Text>
);

export const Subjects = () => {
  return (
    <Box id="how-it-works" bg="#FFFFFF" py={{ base: "64px", md: "96px" }}>
      <Container maxW="1200px" px={{ base: "5", md: "8" }}>

        {/* Section header */}
        <Flex direction="column" align="center" textAlign="center" mb={{ base: 12, md: 16 }}>
          <SectionLabel>The Problem</SectionLabel>
          <Text
            as="h2"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="600"
            fontSize={{ base: "1.75rem", md: "2.375rem" }}
            color="#293452"
            lineHeight="1.25"
            maxW="620px"
          >
            You're studying hard.{" "}
            <Box as="span" color="#0095C8">But are you actually ready?</Box>
          </Text>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }} mb={12}>

          {/* Left — what students use */}
          <Box>
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="0.9375rem"
              color="rgba(41,52,82,0.6)"
              mb={5}
              fontWeight="500"
            >
              Most nursing students rely on:
            </Text>
            <Flex direction="column" gap={3}>
              {painPoints.map((point, i) => (
                <MotionBox
                  key={point}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                >
                  <Flex
                    align="center"
                    gap={4}
                    p={4}
                    bg="#fafafa"
                    borderRadius="10px"
                    border="1px solid #f1f5f9"
                    _hover={{ borderColor: "#e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                    transition="all 0.2s"
                  >
                    <Box
                      flexShrink={0}
                      w="32px"
                      h="32px"
                      borderRadius="8px"
                      bg="#fff5f5"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <AlertTriangle size={15} color="#ef4444" strokeWidth={2} />
                    </Box>
                    <Text
                      fontFamily="'Inter', sans-serif"
                      fontSize="0.9375rem"
                      color="#475569"
                    >
                      {point}
                    </Text>
                  </Flex>
                </MotionBox>
              ))}
            </Flex>
          </Box>

          {/* Right — what they're unsure about */}
          <Box>
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="0.9375rem"
              color="rgba(41,52,82,0.6)"
              mb={5}
              fontWeight="500"
            >
              You cover a lot — but still feel unsure:
            </Text>
            <Flex direction="column" gap={3} mb={8}>
              {doubts.map((doubt, i) => (
                <MotionBox
                  key={doubt}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                >
                  <Flex
                    align="center"
                    gap={4}
                    p={4}
                    bg="#fffbeb"
                    borderRadius="10px"
                    border="1px solid #fef3c7"
                    _hover={{ borderColor: "#fde68a", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                    transition="all 0.2s"
                  >
                    <Box
                      flexShrink={0}
                      w="32px"
                      h="32px"
                      borderRadius="8px"
                      bg="#fef9c3"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <HelpCircle size={15} color="#d97706" strokeWidth={2} />
                    </Box>
                    <Text
                      fontFamily="'Inter', sans-serif"
                      fontSize="0.9375rem"
                      color="#78350f"
                    >
                      {doubt}
                    </Text>
                  </Flex>
                </MotionBox>
              ))}
            </Flex>
          </Box>

        </SimpleGrid>

        {/* Closing line */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Flex justify="center">
            <Box
              bg="#293452"
              borderRadius="12px"
              px={{ base: 7, md: 14 }}
              py={6}
              textAlign="center"
              maxW="520px"
              w="full"
            >
              <Text
                fontFamily="'Public Sans', sans-serif"
                fontSize={{ base: "1.125rem", md: "1.375rem" }}
                fontWeight="600"
                color="white"
                lineHeight="1.4"
              >
                Effort isn't the problem.{" "}
                <Box as="span" bgGradient="linear(135deg, #0095C8, #809CFF)" bgClip="text">
                  Direction is.
                </Box>
              </Text>
            </Box>
          </Flex>
        </MotionBox>

      </Container>
    </Box>
  );
};
